import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { PageHeader } from '@/shell/AppShell'
import { api } from '@/lib/api'
import { BarChart, type BarDatum } from '@/components/BarChart'
import { ErrorState, Skeleton } from '@/components/States'
import { CsvButton } from '@/components/CsvButton'
import { RefreshButton } from '@/components/RefreshButton'
import { downloadCsv } from '@/lib/csv'

// A16.34 — Bot ekotizimi statistikasi. Modul-lokal, alohida fayl (band-fayllar boshqa
// sessiyalar tomonidan tahrirlanayotgani + adapters.ts/i18n HOT emas). Backend REAL:
// GET /admin/analytics/bots (`handlers/admin.go` AnalyticsBots → services BotEcosystemStats).
// PII-siz sof-agregat: users.is_bot + bot_platform.bots/bot_apps/bot_games. Har sanoq
// mustaqil (bittasi yiqilsa 0 qaytadi, ekran buzilmaydi).
interface BotEcosystemData {
  total_bots: number
  official_bots: number
  gateway_bots: number
  active_bots: number
  revoked_bots: number
  mini_app_bots: number
  business_bots: number
  inline_bots: number
  total_apps: number
  total_games: number
  total_active_users: number
}
function fetchBotEcosystem(signal?: AbortSignal): Promise<BotEcosystemData> {
  return api.get<BotEcosystemData>('/admin/analytics/bots', signal ? { signal } : undefined)
}

const STR = {
  title: 'Bot ekotizimi statistikasi',
  note: "Platforma bo‘ylab bot yig‘indilari (rasmiy botlar, gateway botlari, mini-app/inline/biznes qobiliyatlari, ilova/o‘yin va faol foydalanuvchilar). PII-siz agregat.",
  totalBots: 'Jami botlar',
  officialBots: 'Rasmiy botlar',
  gatewayBots: 'Gateway botlari',
  activeBots: 'Faol (bekor qilinmagan)',
  revokedBots: 'Bekor qilingan',
  miniAppBots: 'Mini-App botlari',
  businessBots: 'Biznes botlari',
  inlineBots: 'Inline botlari',
  totalApps: 'Bot ilovalari',
  totalGames: "Bot o‘yinlari",
  totalActiveUsers: 'Faol foydalanuvchilar',
  capabilities: 'Qobiliyatlar bo‘yicha taqsimot',
  assets: 'Aktivlar',
  noData: "Ma’lumot yo‘q",
} as const

function KPI({ label, value, loading, accent }: { label: string; value?: number; loading: boolean; accent?: boolean }) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="text-xs text-tertiary">{label}</div>
      {loading ? (
        <Skeleton className="mt-1.5 h-8 w-16" />
      ) : (
        <div className={`mt-0.5 font-mono text-2xl tabular ${accent ? 'text-accent' : 'text-primary'}`}>
          {value?.toLocaleString() ?? '—'}
        </div>
      )}
    </div>
  )
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="max-w-3xl rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="mb-2 text-sm font-medium text-secondary">{title}</div>
      {children}
    </div>
  )
}

/** A16.34 — Bot ekotizimi statistikasi (bot/mini-app/o‘yin yig‘indilari, REAL backend). */
export function BotEcosystemOverview() {
  const [data, setData] = useState<BotEcosystemData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchBotEcosystem(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])

  const capBars = useMemo<BarDatum[]>(
    () =>
      data
        ? [
            { label: STR.miniAppBots, value: data.mini_app_bots },
            { label: STR.businessBots, value: data.business_bots },
            { label: STR.inlineBots, value: data.inline_bots },
            { label: STR.activeBots, value: data.active_bots },
            { label: STR.revokedBots, value: data.revoked_bots },
          ]
        : [],
    [data],
  )
  const assetBars = useMemo<BarDatum[]>(
    () =>
      data
        ? [
            { label: STR.totalApps, value: data.total_apps },
            { label: STR.totalGames, value: data.total_games },
          ]
        : [],
    [data],
  )

  const handleExport = () => {
    if (!data) return
    downloadCsv('bot-ecosystem', [
      ['metric', 'value'],
      [STR.totalBots, data.total_bots],
      [STR.officialBots, data.official_bots],
      [STR.gatewayBots, data.gateway_bots],
      [STR.activeBots, data.active_bots],
      [STR.revokedBots, data.revoked_bots],
      [STR.miniAppBots, data.mini_app_bots],
      [STR.businessBots, data.business_bots],
      [STR.inlineBots, data.inline_bots],
      [STR.totalApps, data.total_apps],
      [STR.totalGames, data.total_games],
      [STR.totalActiveUsers, data.total_active_users],
    ])
  }

  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
  return (
    <>
      <PageHeader
        title={STR.title}
        screenId="A16.34"
        description={STR.note}
        verified
        actions={
          <div className="flex items-center gap-2">
            <RefreshButton onClick={() => setReload((n) => n + 1)} />
            {data && <CsvButton onClick={handleExport} />}
          </div>
        }
      />
      <div className="min-h-0 flex-1 space-y-3 overflow-auto p-3">
        <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
          <KPI label={STR.totalBots} value={data?.total_bots} loading={loading} accent />
          <KPI label={STR.officialBots} value={data?.official_bots} loading={loading} />
          <KPI label={STR.gatewayBots} value={data?.gateway_bots} loading={loading} />
          <KPI label={STR.totalActiveUsers} value={data?.total_active_users} loading={loading} accent />
          <KPI label={STR.miniAppBots} value={data?.mini_app_bots} loading={loading} />
          <KPI label={STR.businessBots} value={data?.business_bots} loading={loading} />
          <KPI label={STR.inlineBots} value={data?.inline_bots} loading={loading} />
          <KPI label={STR.revokedBots} value={data?.revoked_bots} loading={loading} />
        </div>
        <Panel title={STR.capabilities}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={capBars} emptyLabel={STR.noData} tableLabel={STR.capabilities} />
          )}
        </Panel>
        <Panel title={STR.assets}>
          {loading ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <BarChart data={assetBars} emptyLabel={STR.noData} tableLabel={STR.assets} />
          )}
        </Panel>
      </div>
    </>
  )
}
